<template>
  <el-form
    ref="form"
    label-width="auto"
    size="small"
  >
    <el-form-item
      label="Account"
      :show-message="!event.$fields.account.valid"
      :error="event.$fields.account.message"
      required
    >
      <el-autocomplete
        v-model="accountAutocompleteValue"
        :fetch-suggestions="findAccount"
        :trigger-on-focus="false"
        :debounce="500"
        :disabled="disabledFields.includes('account')"
        clearable
        class="w-100"
        @select="handleAccountSelection"
        @clear="clearAccountSelection"
      >
        <template v-slot="{ item }">
          {{ item.name }}
        </template>
      </el-autocomplete>
    </el-form-item>

    <el-form-item
      label="Priority"
      :show-message="!event.$fields.priority.valid"
      :error="event.$fields.priority.message"
      required
    >
      <el-radio-group
        v-model="event.priority"
        :disabled="disabledFields.includes('priority')"
      >
        <el-radio label="normal">
          <el-tag type="primary">
            Normal
          </el-tag>
        </el-radio>

        <el-radio label="low">
          <el-tag type="success">
            Low
          </el-tag>
        </el-radio>

        <el-radio label="medium">
          <el-tag type="warning">
            Medium
          </el-tag>
        </el-radio>

        <el-radio label="high">
          <el-tag type="danger">
            High
          </el-tag>
        </el-radio>
      </el-radio-group>
    </el-form-item>

    <el-form-item
      label="Due at"
      :show-message="!event.$fields.due_at.valid"
      :error="event.$fields.due_at.message"
      required
    >
      <div style="display: flex;">
        <el-date-picker
          v-model="dueDate"
          type="date"
          placeholder="Pick a due date"
          format="dd/MM/yyyy"
          value-format="yyyy-MM-dd"
          :default-value="defaultDueAt.toDate()"
          :disabled="disabledFields.includes('due_at')"
          class="mr-3"
        />
        <el-time-select
          v-model="dueTime"
          :picker-options="{
            start: '01:00',
            step: '00:15',
            end: '12:45'
          }"
          format="hh:mm"
          value-format="hh:mm"
          placeholder="Pick a due time"
          class="mr-3"
        />
        <el-select v-model="ampm">
          <el-option
            label="AM"
            value="AM"
          />
          <el-option
            label="PM"
            value="PM"
          />
        </el-select>
      </div>
    </el-form-item>

    <el-form-item
      label="Event Type"
      :show-message="!event.$fields.type.valid"
      :error="event.$fields.type.message"
      required
    >
      <el-select
        v-model="event.type"
        placeholder="Select"
        value-key="id"
        :loading="isLoadingEventTypes"
        :disabled="disabledFields.includes('type')"
        class="w-100"
      >
        <el-option
          v-for="type in eventTypes"
          :key="type.id"
          :label="type.name"
          :value="type"
        />
      </el-select>
    </el-form-item>

    <el-form-item
      label="Note"
      :show-message="!note.$fields.content.valid"
      :error="note.$fields.content.message"
      required
    >
      <el-input
        v-model="note.content"
        type="textarea"
        :autosize="{ minRows: 6, maxRows: 8}"
        :disabled="disabledFields.includes('note')"
        clearable
      />
    </el-form-item>
  </el-form>
</template>

<script>
import AccountEvent from '@/plugins/sdk/resources/Account/Event'
import Note from '@/plugins/sdk/resources/Note'
import Filter, { IS } from '@/plugins/sdk/Filter'

export default {
  props: {
    event: {
      type: Object,
      required: true,
      validator: function (value) {
        return value instanceof AccountEvent
      }
    },
    note: {
      type: Object,
      required: true,
      validator: function (value) {
        return value instanceof Note
      }
    },
    disabledFields: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data () {
    return {
      accountAutocompleteValue: undefined,
      isLoadingEventTypes: false,
      defaultDueAt: this.$moment().add(30, 'minutes').startOf('hour'),
      eventTypes: []
    }
  },
  computed: {
    dueDate: {
      set: function (newValue) {
        const time = this.$moment(this.event.due_at).format('HH:mm')
        this.event.due_at = this.$moment(`${newValue} ${time}`, 'YYYY-MM-DD HH:mm').toDate()
      },
      get: function () {
        return this.$moment(this.event.due_at).format('YYYY-MM-DD')
      }
    },
    dueTime: {
      set: function (newValue) {
        const date = this.$moment(this.event.due_at).format('YYYY-MM-DD')
        const ampm = this.$moment(this.event.due_at).format('A')
        this.event.due_at = this.$moment(`${date} ${newValue} ${ampm}`, 'YYYY-MM-DD hh:mm A').toDate()
      },
      get: function () {
        return this.$moment(this.event.due_at).format('hh:mm')
      }
    },
    ampm: {
      set: function (newValue) {
        const date = this.$moment(this.event.due_at).format('YYYY-MM-DD')
        const time = this.$moment(this.event.due_at).format('hh:mm')
        this.event.due_at = this.$moment(`${date} ${time} ${newValue}`, 'YYYY-MM-DD hh:mm A').toDate()
      },
      get: function () {
        return this.$moment(this.event.due_at).format('A')
      }
    }
  },
  watch: {
    'event.account': {
      immediate: true,
      handler (account) {
        if (account instanceof this.$sdk.account) {
          this.accountAutocompleteValue = this.event.account.name
          this.getEventTypes()
        } else {
          this.accountAutocompleteValue = undefined
        }
      }
    },
    'event.priority': {
      immediate: true,
      handler (priority) {
        if (typeof priority === 'undefined') {
          this.event.priority = 'normal'
        }
      }
    }
  },
  methods: {
    getEventTypes () {
      this.isLoadingEventTypes = true

      this
        .$sdk
        .account_event_type
        .list(builder => {
          builder.limit = 1000

          if (this.event.account instanceof this.$sdk.account) {
            builder.filters = [ new Filter('for_account_status', IS, [ null, this.event.account.status ]) ]
          }
        })
        .then(res => { this.eventTypes = res })
        .finally(() => { this.isLoadingEventTypes = false })
    },
    findAccount (query, cb) {
      if (query) {
        this
          .$sdk
          .search(query, this.$sdk.account)
          .then(cb)
      }
    },
    handleAccountSelection (account) {
      this.event.account = account
    },
    clearAccountSelection () {
      this.accountAutocompleteValue = undefined

      if (this.event.account instanceof this.$sdk.account) {
        this.event.account.reset()
      }
    }
  }
}
</script>
